import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    minusButton.disabled = value <= min;
    plusButton.disabled = false;
  }

  increaseQuantity(event) {
    const effectiveMax = this.getEffectiveMax();
    const { value } = this.getCurrentValues();
    if (effectiveMax !== null && value >= effectiveMax) {
      event?.preventDefault?.();
      this.#showMaxError(effectiveMax);
      return;
    }
    this.#hideMaxError();
    super.increaseQuantity(event);
  }

  setQuantity(event) {
    if (event?.target instanceof HTMLInputElement) {
      const requested = parseInt(event.target.value) || 0;
      const effectiveMax = this.getEffectiveMax();
      if (effectiveMax !== null && requested > effectiveMax) {
        this.#showMaxError(effectiveMax);
      } else {
        this.#hideMaxError();
      }
    }
    super.setQuantity(event);
  }

  #showMaxError(maxQuantity) {
    const row = this.closest('tr');
    if (!row) return;
    const container = row.querySelector('.cart-items__error');
    const textEl = row.querySelector('.cart-item__error-text');
    if (!container || !textEl) return;

    const template =
      (window.Theme && Theme.translations && Theme.translations.quantity_error_max) ||
      'Only {{ maximum }} left in stock';
    textEl.textContent = template.replace('{{ maximum }}', String(maxQuantity));
    container.classList.remove('hidden');

    clearTimeout(this._maxErrorTimer);
    this._maxErrorTimer = setTimeout(() => this.#hideMaxError(), 6000);
  }

  #hideMaxError() {
    const row = this.closest('tr');
    if (!row) return;
    const container = row.querySelector('.cart-items__error');
    if (container) container.classList.add('hidden');
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
